// Shared exam diagnoses - the catalogue loaded once at login, and the rows stored
// per exam.

/**
 * What the API sends. Tuples rather than objects: with ~18k diagnoses and ~33k
 * memberships, repeating the property names would add roughly 450 KB of pure key
 * text to a payload every user downloads.
 */
export interface DiagnosisCatalogDto {
  /** [ID, ParentID, Label] */
  categories: [number, number | null, string][];
  /** [ID, Code, Name] */
  diagnoses: [number, string | null, string][];
  /** [CategoryID, DiagnosisID] */
  items: [number, number][];
}

/**
 * One searchable diagnosis, as held in memory after login. The catalogue is keyed
 * on (name, code), not name alone: in ICD-9 the site decides the code, so the same
 * wording can be two different diagnoses. "Aortic stenosis" is 424.1 as acquired
 * valve disease but 746.3 as a congenital malformation, and both are in the list.
 */
export interface DiagnosisEntry {
  id: number;
  /** name - not unique on its own, see above */
  n: string;
  /** ICD code - stored on the exam, never shown to the doctor */
  c: string | null;
  /**
   * "Oncology > Skin". A diagnosis usually sits in several categories - a bone
   * tumour is listed under every anatomical site - so this is the first one. It
   * fills ExamDiagnoses.Path, and it is what tells two same-named entries apart
   * in the search list, so the option template must keep showing it.
   */
  p: string | null;
  /** lowercased name, precomputed once on load so filtering does not rebuild it per keystroke */
  _l: string;
}

/**
 * A node in the drill-down tree. Levels are materialised one at a time, on click:
 * expanding the whole tree up front would mean ~34k objects for a panel most
 * exams never open.
 */
export interface DiagnosisNode {
  label: string;
  /** set on a category, so the service can fetch the next level */
  catId?: number;
  /** whether clicking drills down or picks */
  hasChildren?: boolean;
  /** set on a diagnosis, absent on a category */
  entry?: DiagnosisEntry;
}

/** A diagnosis picked on an exam, as stored in ExamDiagnoses. */
export interface ExamDiagnosis {
  Code: string | null;
  Name: string;
  Path: string | null;
  SortOrder?: number;
  /** 0 = main diagnosis (default), 1 = secondary diagnosis */
  Kind?: 0 | 1;
}

/** Auto Text library id for the diagnoses notes. Highest in use elsewhere is 20022. */
export const DIAGNOSIS_NOTES_AUTOTEXT_ID = 21000;
